import React, { useContext, useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";
import { Modal } from "notes";
import { RegistrationForm } from "./Components";
import { VideoModal } from "Components";
import { Layout } from "../Layout";
import { fonts } from "theme/fonts";
import { Duration } from "luxon";
import {
  TimeContext,
  CountdownSmall as Countdown,
  SSOButton,
  TextReminder,
  AnalyticsContext,
} from "Components";
import playIcon from "Images/play-button.png";

export const Register = React.memo(({ event, onSuccess }) => {
  const { time } = useContext(TimeContext);
  const { logClick, logVideoAction, logLeadSubmission } = useContext(
    AnalyticsContext
  );
  const [cookies, setCookie] = useCookies();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [video, setVideo] = useState(null);

  const [eventFired, setEventFired] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get("utm_source");
  useEffect(() => {
    if (utm_source === "oauth" && !eventFired) {
      console.log("Firing form submit");
      setEventFired(true);
      logLeadSubmission({ formPart: 0 });
    }
  }, []);

  let RegFormContent;

  const api = process.env.REACT_APP_FIRE_BASE_STORAGE_API;
  const storageDirectory = event.storageDirectory;

  let regBg = `${api}/o/${storageDirectory}%2F${event?.backgrounds?.registration}?alt=media`;
  let headerLogo = `${api}/o/${storageDirectory}%2F${event?.logos?.header?.path}?alt=media`;
  let desktopImage = `${api}/o/${storageDirectory}%2F${event?.register?.artistImageDesktop?.path}?alt=media`;
  let mobileImage = `${api}/o/${storageDirectory}%2F${event?.register?.artistImageMobile?.path}?alt=media`;

  const { videoLink: ctaVideoLink } = event.register.callToActions.primary;

  const headerLogoLink = event.logos.header.link;

  const startDate = event.start.toFormat("LLLL d");

  const logoClickHandler = () => {
    logClick({
      label: "Header Logo",
      url: headerLogoLink,
    });
  };

  const handleSuccess = (admin, token, formPart, email) => {
    onSuccess(admin, token, formPart);
    if (formPart === 1) {
      setCookie("email", email, { maxAge: 31622400 });
      setFormData({ email });
    }
  };

  const setModalVisible = (bool, video) => {
    setVideo(video);
    setOpen(bool);
    logVideoAction({
      bool,
      videoLink: video,
      videoTitle: "Hero Video",
    });
  };

  if (event.campaignEnd < time) {
    RegFormContent = (
      <>
        <ClosedHeading>
          {ReactHtmlParser(event.postShow.closedHeading)}
        </ClosedHeading>
        <ClosedBody>{ReactHtmlParser(event.postShow.closedBody)}</ClosedBody>
      </>
    );
  } else if (cookies?.formPart == 2) {
    const {
      heading,
      subHeading,
      body,
      countdownHeading,
      button,
    } = event.register.content.part2;

    const timeToStart =
      time < event.start &&
      Duration.fromMillis(event.start - time).shiftTo(
        "days",
        "hours",
        "minutes",
        "seconds",
        "milliseconds"
      );

    const timer = {
      label: countdownHeading,
      days: timeToStart.days,
      hours: timeToStart.hours,
      minutes: timeToStart.minutes,
      seconds: timeToStart.seconds,
    };

    RegFormContent = (
      <>
        {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
        {subHeading && <SubHeading>{ReactHtmlParser(subHeading)}</SubHeading>}
        {body && <Body>{ReactHtmlParser(body)}</Body>}
        <SmallCountdown>
          <Countdown {...timer} />
        </SmallCountdown>
        <TextReminder buttonText={"Text Reminder"} />
      </>
    );
  } else {
    const { heading, eventTime, button } = event.register.content.part1;

    RegFormContent = (
      <>
        {heading && <Heading>{ReactHtmlParser(heading)}</Heading>}
        <EventInfo>
          <EventDate>
            {startDate}
            <sup>th</sup>
          </EventDate>
          <EventTime>{ReactHtmlParser(eventTime)}</EventTime>
        </EventInfo>
        <SSOButton buttonText={button} />
      </>
    );
  }

  return (
    <Layout
      registration
      showHeader={false}
      heroContent={false}
      noPadding
      showFooter
    >
      <Registration formPart={cookies.formPart} background={regBg}>
        <Content as="section" id="registration-content">
          <ContentContainer>
            <Link
              href={headerLogoLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={logoClickHandler}
            >
              <Logo src={headerLogo} />
            </Link>

            <FormContent>{RegFormContent}</FormContent>
          </ContentContainer>
        </Content>

        <ArtistImage
          as="section"
          id="artist-image"
          desktopImage={desktopImage}
          // mobileImage={mobileImage}
        >
          <OverlayContainer>
            <OverlayImage
              src={playIcon}
              onClick={() => setModalVisible(true, ctaVideoLink)}
            />
          </OverlayContainer>
        </ArtistImage>
      </Registration>

      {/* <VideoModal size="large" open={open} setOpen={setModalVisible}>
        <VideoModalContainer>
          <Video
            src={video}
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
        </VideoModalContainer>
      </VideoModal> */}

      <VideoModal
        size="large"
        open={open}
        setOpen={setModalVisible}
        path={ctaVideoLink}
        autoplay={true}
      />
    </Layout>
  );
});

// ======== CONTENT / LEFT SIDE ======== //

const Registration = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  width: 100%;

  ${(props) =>
    props.background &&
    `
    background-image: url(${props.background});
    background-size: cover;
    background-position: center;
  `}

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    flex-direction: row;

    ${(props) =>
      props.background &&
      `
      background-position: left center;
    `}
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  margin: 0 auto;
  padding: 25px 15px 50px;
  width: 100%;
  max-width: 450px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    max-width: unset;
    padding: 25px 50px 25px 200px;
    max-width: 600px;
    min-height: 700px;
  }
`;

const Link = styled.a`
  margin: 25px 0;
`;

const Logo = styled.img`
  width: 100%;
  max-width: 200px;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
`;

const Heading = styled.h2`
  font-size: 22px;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ea1300;

  span {
    font-weight: 300;
    @media only screen and ${(props) => props.theme.maxMedia.small} {
      font-size: 25px;
      display: block;
    }
  }
`;

const ClosedHeading = styled(Heading)``;

const SubHeading = styled.h3`
  font-size: 22px;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ea1300;
`;

const Body = styled.p`
  font-weight: 100;
  margin-top: 0;

  a,
  a:active,
  a:visited {
    color: inherit;
  }
`;

const ClosedBody = styled.p`
  color: #ea1300;
  font-size: 20px;
`;

const SmallCountdown = styled.div`
  width: 100%;
  margin-bottom: 24px;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    width: unset;
    padding: 30px 25px 0 0;
    margin: 0;
  }
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: row;
  font-size: clamp(16px, 6vw, 32px);
  font-weight: 600;
  width: 100%;
  line-height: 1.15;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    font-size: clamp(16px, 8vw, 26px);
  }
`;

const EventDate = styled.div`
  padding: 0 15px 0 0;
  text-transform: uppercase;
`;

const EventTime = styled.div`
  border-left: 1px solid #df2a2e;
  display: flex;
  flex-direction: column;
  padding: 0 0 0 15px;
`;

// ======== ARTIST IMAGE / RIGHT SIDE ======== //

const ArtistImage = styled.div`
  align-items: flex-start;
  background-image: url(${(props) => props.desktopImage});
  background-repeat: no-repeat;
  background-size: contain;
  padding-top: ${(9 / 16) * 100}%;
  position: relative;
  background-position: bottom;

  @media only screen and ${(props) => props.theme.maxMedia.medium} {
    flex-grow: 1;
    padding-top: 0;
  }
`;

const OverlayContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const OverlayImage = styled.img`
  width: clamp(50px, 15vw, 100px);
  cursor: pointer;
`;

// ======== MODAL ======== //

// const VideoModal = styled(Modal)`
//   justify-content: center;
//   min-height: unset;
//   position: relative;

//   svg {
//     top: 10px;
//     right: 10px;
//   }
// `;

// const VideoModalContainer = styled.div`
//   padding-top: 56.25%;
//   position: relative;
// `;

// const Video = styled.iframe`
//   height: 100%;
//   position: absolute;
//   top: 0;
//   width: 100%;
//   border: 0;
// `;
